'use client';

import Link from 'next/link';
import React, { forwardRef, type MutableRefObject, type Ref, type RefCallback } from 'react';
import { type UseLinkPrefetchOptions, useLinkPrefetch } from '../../hooks/useLinkPrefetch';

// Compose two event handlers safely
const composeHandlers = <T extends (...args: any[]) => void>(
  original?: T,
  additional?: T,
): T | undefined => {
  if (!original && !additional) return undefined;
  if (!original) return additional;
  if (!additional) return original;

  return ((...args: Parameters<T>) => {
    original(...args);
    additional(...args);
  }) as T;
};

// Compose two refs safely
const composeRefs = <T,>(...refs: (Ref<T> | undefined)[]): RefCallback<T> | null => {
  const validRefs = refs.filter(Boolean) as Ref<T>[];
  if (validRefs.length === 0) return null;

  return (node: T | null) => {
    validRefs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        (ref as MutableRefObject<T | null>).current = node;
      }
    });
  };
};

interface PrefetchLinkProps
  extends Omit<React.ComponentProps<typeof Link>, 'href' | 'prefetch'>,
    UseLinkPrefetchOptions {
  href: string;
}

export const PrefetchLink = forwardRef<HTMLAnchorElement, PrefetchLinkProps>(
  (
    {
      href,
      prefetchOnHover = true,
      prefetchWhenVisible = true,
      rootMargin = '400px',
      hoverDelayMs = 80,
      maxConcurrent = 4,
      children,
      onPointerEnter,
      onPointerLeave,
      onFocus,
      ...linkProps
    },
    ref,
  ) => {
    const prefetchBind = useLinkPrefetch({
      href,
      prefetchOnHover,
      prefetchWhenVisible,
      rootMargin,
      hoverDelayMs,
      maxConcurrent,
    });

    return (
      <Link
        href={href}
        prefetch={false} // Disable Next.js prefetch to avoid double work
        {...linkProps}
        ref={composeRefs(ref, prefetchBind.ref)}
        onPointerEnter={composeHandlers(onPointerEnter, prefetchBind.onPointerEnter)}
        onPointerLeave={composeHandlers(onPointerLeave, prefetchBind.onPointerLeave)}
        onFocus={composeHandlers(onFocus, prefetchBind.onFocus)}
      >
        {children}
      </Link>
    );
  },
);

PrefetchLink.displayName = 'PrefetchLink';
